import { useState } from 'react';
import { SimpleGrid, Badge, Avatar, Group, Text, Modal, Divider, Button, Stack, Flex } from '@mantine/core';
import { IconBook, IconBrandWhatsapp, IconEye, IconLock, IconPlayerPlay, IconPlus, IconQuote } from '@tabler/icons-react';
import './SubscriptionPreviewContent.css';

const assets = {
   workshop: {
      thumbnail: 'https://forvet-main.s3.sa-east-1.amazonaws.com/public/images/system/workshop-comunidad-septiembre-2026.jpeg',
      video: 'https://forvet-main.s3.sa-east-1.amazonaws.com/public/videos/workshop-comunidad-sept-2026.mp4',
      teacher_info: 'https://forvet-main.s3.sa-east-1.amazonaws.com/public/images/system/workshop-comunidad-sept-2026-description.jpeg'
   }
}


const FREE_WORKSHOP = {
   title: 'Comunicación clínica: Herramientas prácticas para fortalecer la relación veterinaria–tutor–paciente en pequeños animales',
   speaker: 'M.V. Martin Quero',
   thumbnail: assets.workshop.thumbnail,
   videoUrl: assets.workshop.video,
   mv_thumbnail: assets.workshop.teacher_info,
   description: 'Este workshop teórico-práctico está diseñado para que médicos veterinarios clínicos y estudiantes avanzados de Medicina Veterinaria adquieran herramientas conversacionales concretas orientadas al “saber hacer”, combinando modelos internacionales de comunicación clínica (Calgary-Cambridge, 4E y PEARLS) con técnicas del coaching ontológico. A través del análisis de situaciones reales de consulta y una actividad integradora bajo el formato dinámico de FORVET, los participantes aprenderán a estructurar la consulta con eficiencia, construir confianza con el tutor desde el primer momento, gestionar conversaciones complejas o difíciles y lograr una toma de decisiones compartida que incremente de forma directa la adherencia a los tratamientos de sus pacientes.',
   tag: 'Próximo workshop',
   date: '26 de Septiembre'
};

const NEWSLETTER_PREVIEW = {
   eyebrow: 'FORVET al día',
   edition: 'N° 02',
   month: 'Julio 2026',
   title: 'El coraje de hacer menos',
   tagline: 'Un mes en el que los profes coincidieron sin ponerse de acuerdo: la destreza clínica no siempre es intervenir más.',
   // Teaser muy corto, cortado a media frase — versión "pobre" para no-suscriptos
   teaser: 'Este mes pasó algo raro. Sin hablar entre ellos, casi todos los docentes arrancaron sus clases por el mismo lado: no por la técnica ni por el fármaco, sino por el error. Y el error, clase tras clase, era el mismo con distintos disfraces: hacer de más...',
   // Contenido real que se muestra en el modal (editorial completo)
   fullEditorial: [
      'Este mes pasó algo raro. Sin hablar entre ellos, casi todos los docentes arrancaron sus clases por el mismo lado: no por la técnica ni por el fármaco, sino por el error. Y el error, clase tras clase, era el mismo con distintos disfraces: hacer de más.',
      'Medicar un estadio B1 "por las dudas". Correr al quirófano una obstrucción uretral que se resolvía sola. Dar corticoides antes de tener el diagnóstico. Cargar contra el síntoma sin mirar la causa. Ninguno lo llamó así, pero todos, a su manera, estaban respondiendo a la misma pregunta:',
   ],
   editorialQuestion: '¿Cuándo hacer menos es, en realidad, hacer mejor?',
   // Temario — se muestra bloqueado, solo para mostrar cuánto contenido hay
   sections: [
      { number: '01', label: 'Editorial', desc: 'El hilo del mes' },
      { number: '02', label: 'Voz del docente', desc: 'Lo que dijo el profe' },
      { number: '03', label: 'Ronda de especialidades', desc: '6 especialidades, un insight cada una' },
      { number: '04', label: 'Comunidad', desc: 'Debates, casos clínicos y red de apoyo' },
      { number: '05', label: 'Próximo workshop', desc: 'Toda la info del encuentro de agosto' },
      { number: '06', label: 'Novedades FORVET', desc: 'Lo nuevo de la comunidad' },
   ],
};

// const TESTIMONIALS = [
//    {
//       name: 'Martín Ibarra',
//       role: 'Veterinario clínico, Córdoba',
//       quote: 'Los workshops mensuales me sirvieron para actualizar protocolos que tenía hace años sin revisar.',
//       avatar: null,
//    },
//    {
//       name: 'Rocío Paz',
//       role: 'Veterinaria rural, Santa Fe',
//       quote: 'Lo que más valoro es poder discutir casos con colegas que no tengo cerca físicamente.',
//       avatar: null,
//    },
//    {
//       name: 'Federico Suárez',
//       role: 'Veterinario de pequeños animales',
//       quote: 'El newsletter es corto y va al grano. Lo leo siempre, no es spam.',
//       avatar: null,
//    },
// ];

const TESTIMONIALS = [
   {
      type: 'video',
      name: 'Yoana Scrivanti',
      role: 'Médica veterinaria',

      avatar: "https://forvet-main.s3.sa-east-1.amazonaws.com/public/images/comunidad/comunidad-thumb-test.png",
      thumbnail: "https://forvet-main.s3.sa-east-1.amazonaws.com/public/images/comunidad/comunidad-thumb-test.png",
      videoUrl: 'https://forvet-main.s3.sa-east-1.amazonaws.com/public/misc/testimonio_01.mp4',
   },
];

export default function SubscriptionPreviewContent({ featuredPlan, isLoadingPlans, onInscription }) {
   const [modalOpened, setModalOpened] = useState(null); // null | 'workshop' | 'newsletter' | 'video' | 'testimonial'
   const [activeTestimonial, setActiveTestimonial] = useState(null);

   const openTestimonialVideo = (testimonial) => {
      setActiveTestimonial(testimonial);
      setModalOpened('testimonial');
   };


   return (
      <div className="SubscriptionPreview-container">

         <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} className="SubscriptionPreview-grid">

            {/* Workshop de muestra — abierto */}
            <div className="PreviewCard PreviewCard--open">
               <Badge color="var(--color-tertiary)" mb={8}>{FREE_WORKSHOP.tag}</Badge>

               <div className="PreviewCard-thumbnail" onClick={() => {
                  if(!FREE_WORKSHOP.videoUrl) return
                  setModalOpened('video')}}>
                  <img src={FREE_WORKSHOP.thumbnail} alt={FREE_WORKSHOP.title} />
                 {FREE_WORKSHOP.videoUrl && <div className="PreviewCard-playOverlay">
                     <IconPlayerPlay size={28} />
                  </div>}
               </div>

               <h3 className="PreviewCard-title">{FREE_WORKSHOP.title}</h3>
               <p className="PreviewCard-meta">{FREE_WORKSHOP.speaker} · {FREE_WORKSHOP.date}</p>
               <Divider variant='dashed' mt={4} />
               <div className="PreviewCard-textWrapper">
                  <p className="PreviewCard-excerpt">{FREE_WORKSHOP.description}</p>
                  <div className="PreviewCard-fade" />
               </div>

               <div className="PreviewCard-lockedFooter" onClick={() => setModalOpened('workshop')}>
                  <IconEye size={16} />
                  <span>Ver más</span>
               </div>
            </div>

            {/* Newsletter — bloqueado con blur */}
            <div style={{ height: 'max-content' }} className="PreviewCard PreviewCard--locked">
               <Badge color="var(--color-tertiary)" mb={8}>
                  Newsletter · {NEWSLETTER_PREVIEW.edition} · {NEWSLETTER_PREVIEW.month}
               </Badge>
               <h3 className="PreviewCard-title">{NEWSLETTER_PREVIEW.title}</h3>

               <p className="PreviewCard-excerpt">{NEWSLETTER_PREVIEW.teaser}</p>

               <strong style={{ marginTop: 12 }} className='PreviewCard-excerpt'>Temario:</strong>

               <ul className="NewsletterToc">
                  {NEWSLETTER_PREVIEW.sections.map((s) => (
                     <li className="NewsletterToc-item" key={s.number}>
                        {/* <IconLock size={12} className="NewsletterToc-lockIcon" /> */}
                        <span className="NewsletterToc-label">{s.number} · {s.label}</span>
                     </li>
                  ))}
               </ul>

               <div className="PreviewCard-lockedFooter" onClick={() => setModalOpened('newsletter')}>
                  <IconBook size={16} />
                  <span>Leer</span>
               </div>
            </div>

         </SimpleGrid>

         {/* Testimonios */}
         {/* <div className="SubscriptionPreview-testimonials">
            {TESTIMONIALS.map((t, i) => (
               <div className="TestimonialCard" key={i}>
                  <IconQuote size={20} className="TestimonialCard-icon" />
                  <p className="TestimonialCard-quote">"{t.quote}"</p>
                  <Group gap={8} mt={12}>
                     <Avatar radius="xl" size={32} color="teal">{t.name.charAt(0)}</Avatar>
                     <div>
                        <Text c='var(--color-text-primary)' size="sm" fw={600}>{t.name}</Text>
                        <Text c='var(--color-text-primary)' size="xs">{t.role}</Text>
                     </div>
                  </Group>
               </div>
            ))}
         </div> */}
         {/* Testimonios */}
         <Stack>
            <h3 className='SubscriptionPreview-testimonials--title'>Lo que dicen de nuestra comunidad</h3>
            <div className="SubscriptionPreview-testimonials">
            {TESTIMONIALS.map((t, i) => (
               t.type === 'video' ? (
                  <div
                     className="TestimonialCard TestimonialCard--video"
                     key={i}
                     onClick={() => openTestimonialVideo(t)}
                  >
                     <div className="TestimonialCard-videoThumb" style={{
                        backgroundImage: t.thumbnail ? `url(${t.thumbnail})` : "unset"
                     }}>
                        <video
                           src={t.videoUrl}
                           preload="metadata"
                           muted
                           playsInline
                        />
                        <div className="TestimonialCard-playOverlay">
                           <IconPlayerPlay size={22} />
                        </div>
                     </div>
                     <Group gap={8} mt={12}>
                        <Avatar radius="xl" size={32} color="teal" src={t.avatar}>{t.name.charAt(0)}</Avatar>
                        <div>
                           <Text c='var(--color-text-primary)' size="sm" fw={600}>{t.name}</Text>
                           <Text c='var(--color-text-primary)' size="xs">{t.role}</Text>
                        </div>
                     </Group>
                  </div>
               ) : (
                  <div className="TestimonialCard" key={i}>
                     <IconQuote size={20} className="TestimonialCard-icon" />
                     <p className="TestimonialCard-quote">"{t.quote}"</p>
                     <Group gap={8} mt={12}>
                        <Avatar radius="xl" size={32} color="teal">{t.name.charAt(0)}</Avatar>
                        <div>
                           <Text c='var(--color-text-primary)' size="sm" fw={600}>{t.name}</Text>
                           <Text c='var(--color-text-primary)' size="xs">{t.role}</Text>
                        </div>
                     </Group>
                  </div>
               )
            ))}
         </div>
            
         </Stack>

         {/* Modal Video — grande, sin recortes */}
         <Modal
            opened={modalOpened === 'video'}
            onClose={() => setModalOpened(null)}
            size="xl"
            centered
            padding={0}
            withCloseButton
            classNames={{ content: 'VideoModal-content', body: 'VideoModal-body' }}
         >
            <video
               className="VideoModal-video"
               src={FREE_WORKSHOP.videoUrl}
               controls
               autoPlay
            />
         </Modal>

         <Modal
            opened={modalOpened === 'testimonial'}
            onClose={() => { setModalOpened(null); setActiveTestimonial(null); }}
            size="lg"
            centered
            padding={0}
            withCloseButton
            classNames={{ content: 'VideoModal-content', body: 'VideoModal-body' }}
         >
            {activeTestimonial && (
               <>
                  <video
                     className="VideoModal-video"
                     src={activeTestimonial.videoUrl}
                     controls
                     autoPlay
                  />
                  <div className="VideoModal-testimonialFooter">
                     <Text c="#fff" size="sm" fw={600}>{activeTestimonial.name}</Text>
                     <Text c="rgba(255,255,255,0.7)" size="xs">{activeTestimonial.role}</Text>
                  </div>
               </>
            )}
         </Modal>

         {/* Modal detalle Workshop — todo menos el video */}
         <Modal
            opened={modalOpened === 'workshop'}
            onClose={() => setModalOpened(null)}
            title={FREE_WORKSHOP.tag}
            classNames={{
               body: "SubscriptionPreviewContent--workshop-modal-container",
               title: "SubscriptionPreviewContent-modal--title"
            }}
            size="lg"
            centered
         >
            <Stack w='100%'>
               <img
                  src={FREE_WORKSHOP.thumbnail}
                  alt={FREE_WORKSHOP.title}
                  className="Modal-thumbnail"
               />
               <p className="Modal-meta">{FREE_WORKSHOP.speaker} · {FREE_WORKSHOP.date}</p>
               <Divider variant='dashed' />
               <p className="Modal-description">{FREE_WORKSHOP.description}</p>
               <Divider variant='dashed' />
               <p className="Modal-description">¿Quién va a estar a cargo?</p>

               <img
                  src={FREE_WORKSHOP.mv_thumbnail}
                  alt={FREE_WORKSHOP.speaker}
                  className="Modal-teacherInfo"
               />
               <Flex justify='center' gap={12} mt={32} >
                  <Button
                     component="a"
                     href="https://wa.me/+5493512086545?text=¡Hola!%20Quiero%20consultar%20sobre%20la%20membresía%20de%20la%20comunidad"
                     target="_blank"
                     leftSection={<IconBrandWhatsapp size={18} />}
                     variant="outline"
                  >
                     Consultar a un asesor
                  </Button>
                  <Button
                     w="60%"
                     type="button"
                     onClick={() => featuredPlan && onInscription(featuredPlan)}
                     loading={isLoadingPlans}
                     disabled={!isLoadingPlans && !featuredPlan}
                  >
                     Quiero mi lugar
                  </Button>
               </Flex>
            </Stack>
         </Modal>

         {/* Modal detalle Newsletter — contenido completo + CTA */}
         <Modal
            classNames={{
               title: "SubscriptionPreviewContent-modal--title"
            }}
            opened={modalOpened === 'newsletter'}
            onClose={() => setModalOpened(null)}
            title={`${NEWSLETTER_PREVIEW.eyebrow} · ${NEWSLETTER_PREVIEW.edition} · ${NEWSLETTER_PREVIEW.month}`}
            size="lg"
            centered
         >
            <h3 className="Modal-title">{NEWSLETTER_PREVIEW.title}</h3>
            <p className="Modal-tagline">{NEWSLETTER_PREVIEW.tagline}</p>

            {NEWSLETTER_PREVIEW.fullEditorial.map((p, i) => (
               <p className="Modal-description" key={i}>{p}</p>
            ))}
            <p className="Modal-editorialQuestion">{NEWSLETTER_PREVIEW.editorialQuestion}</p>

            <ul className="NewsletterToc NewsletterToc--modal">
               {NEWSLETTER_PREVIEW.sections.slice(1).map((s) => (
                  <li className="NewsletterToc-item" key={s.number}>
                     <IconLock size={16} style={{ flexShrink: 0 }} className="NewsletterToc-lockIcon" />
                     <div>
                        <span className="NewsletterToc-label">{s.number} · {s.label}</span>
                        <span className="NewsletterToc-desc">{s.desc}</span>
                     </div>
                  </li>
               ))}
            </ul>

            <Divider variant='dashed' color='#ffffff19' />

            <div className="Modal-subscribeNotice">
               <IconLock size={24} style={{ flexShrink: 0 }} />
               <p >
                  Leíste el editorial completo. <br />El resto de esta edición <em>—y todas las anteriores
                     y próximas—</em> está disponible solo para suscriptos.
               </p>
            </div>
            <Flex justify='center' gap={12} mt={32} >
               <Button
                  component="a"
                  href="https://wa.me/+5493512086545?text=¡Hola!%20Quiero%20consultar%20sobre%20la%20membresía%20de%20la%20comunidad"
                  target="_blank"
                  leftSection={<IconBrandWhatsapp size={18} />}
                  variant="outline"
               >
                  Consultar a un asesor
               </Button>
               <Button
                  w="60%"
                  type="button"
                  onClick={() => featuredPlan && onInscription(featuredPlan)}
                  loading={isLoadingPlans}
                  disabled={!isLoadingPlans && !featuredPlan}
               >
                  Quiero acceder
               </Button>
            </Flex>
         </Modal>

      </div>
   );
}